<script setup lang="ts">
import { computed, toRef } from "vue";
import { storeToRefs } from "pinia";
import draggable from "vuedraggable";
import { GripVertical } from "lucide-vue-next";
import { Select } from "@/components/episto-ui/select";
import { useResultsV2CardsStore } from "@stores/survey/resultsV2/resultsV2.cards";
import { useCardLocalOptions } from "@/composables/useCardLocalOptions";
import { KPI_TREND_CHART_TYPE_OPTIONS } from "./constants";
import { POPULATION_AUTO_COLORS } from "@/lib/chartPalettes";
import ChartTypeField from "./CardOptionFields/ChartTypeField.vue";
import MetricField from "./CardOptionFields/MetricField.vue";
import ColorToggleField from "./CardOptionFields/ColorToggleField.vue";
import OptionRow from "./CardOptionFields/OptionRow.vue";
import OptionSection from "./CardOptionFields/OptionSection.vue";

// Default trend line color = the "All" population color (when not customized).
const DEFAULT_COLOR = POPULATION_AUTO_COLORS[0];

const props = defineProps({
  card: { type: Object, required: true },
});

const cardRef = toRef(props, "card");
const cardsStore = useResultsV2CardsStore();
const { store, localOptions, updateLocalOption } = useCardLocalOptions(cardRef);
const { savedPopulations } = storeToRefs(store);

// --- Single metric ---

const DEFAULT_METRIC = { type: "options_share", question_id: null, option_codes: [] };
const metric = computed(() => props.card.content?.metric || DEFAULT_METRIC);

function updateMetric(newMetric) {
  cardsStore.updateCardLocal(props.card.id, {
    content: { ...props.card.content, metric: newMetric },
  });
}

function setColor(color) {
  updateMetric({ ...metric.value, color });
}

// --- Populations (the waves = X axis) ---

const savedPops = computed(() =>
  savedPopulations.value.map((sp) => ({ token: sp.token, label: sp.name })).filter((p) => p.token),
);

const populationOptions = computed(() => savedPops.value.map((p) => ({ value: p.token, label: p.label })));

const selectedTokens = computed(() =>
  Array.isArray(localOptions.value.populations) ? localOptions.value.populations : [],
);

const orderedPops = computed(() =>
  selectedTokens.value.map((t) => savedPops.value.find((p) => p.token === t)).filter(Boolean),
);

// Preserve the existing order for kept tokens, append newly selected at the end.
function setSelected(tokens) {
  const current = selectedTokens.value;
  const kept = current.filter((t) => tokens.includes(t));
  const added = tokens.filter((t) => !current.includes(t));
  updateLocalOption("populations", [...kept, ...added]);
  for (const token of added) {
    const sp = savedPopulations.value.find((s) => s.token === token);
    if (sp) store.fetchPopulationChartData(sp);
  }
}

function onReorder(list) {
  updateLocalOption("populations", list.map((p) => p.token));
}
</script>

<template>
  <OptionSection
    id="data"
    title="Data">
    <MetricField :metric="metric" @update="updateMetric" />

    <OptionRow
      label="Populations (X axis)"
      hint="The saved populations plotted along the X axis, in this order. Create saved populations to plot waves.">
      <div class="space-y-2">
      <Select
        :model-value="selectedTokens"
        :options="populationOptions"
        multiple
        :placeholder="savedPops.length ? 'Select populations...' : 'No saved populations yet'"
        @update:model-value="setSelected" />
      <draggable
        v-if="orderedPops.length"
        :model-value="orderedPops"
        item-key="token"
        handle=".pop-drag"
        class="space-y-1"
        @update:model-value="onReorder">
        <template #item="{ element }">
          <div class="flex items-center gap-2 rounded-md border bg-muted/30 px-2 py-1.5">
            <GripVertical class="pop-drag h-4 w-4 cursor-grab text-muted-foreground active:cursor-grabbing" />
            <span class="text-xs">{{ element.label }}</span>
          </div>
        </template>
      </draggable>
      </div>
    </OptionRow>
  </OptionSection>

  <OptionSection
    id="display"
    title="Display"
    :count="[!!card.options?.chart_type, !!metric.color].filter(Boolean).length">
    <ChartTypeField
      :card="card"
      :options="KPI_TREND_CHART_TYPE_OPTIONS"
      default-value="line"
      hint="How the values are drawn across the waves." />

    <ColorToggleField
      :model-value="metric.color ?? null"
      :default-color="DEFAULT_COLOR"
      hint="Color of the trend. It starts as the color of All."
      @update:model-value="setColor" />
  </OptionSection>
</template>
